import { Address, OrganizationContact } from './contact';
import { InvoiceDelivery, InvoicingType, InvoicePeriod } from './invoice';
import { LocaleFormat, LocaleLanguage } from './locale-format';
import { Organization } from './organization';
import { TransportProvider } from './transport';
import { DentistType } from './user';

export type ContactInformation = {
    name: string;
    email: string;
    phone: string;
};

export type InvoiceSettings = {
    emailSettings: EmailSettings;
    type?: InvoicingType;
    paymentTerms?: number;
    invoiceFee?: Monetary;
    deliveryFee?: Monetary;
    invoiceDelivery?: InvoiceDelivery;
    invoicePeriod?: InvoicePeriod;
    invoiceText?: string;
    fortnoxCustomerNo?: string;
    vismaCustomerId?: string;
    contactInformation?: ContactInformation;
    showPatientPIN?: boolean;
    isDeliveryNoteAttached?: boolean;
    isDeliveryNotesMergedWithInvoice?: boolean;
    invoiceAddress: Address;
    supplierReference?: string;
    isOverwritten?: boolean;
};

export type EmailSettings = {
    replyAddress?: string;
    to?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    body?: string;
    creditSubject?: string;
    creditBody?: string;
};

export type Monetary = {
    price?: number;
    currency?: string;
};

export const enum ClientType {
    SUPPLIER = 'SUPPLIER',
    CUSTOMER = 'CUSTOMER',
}

export type ClientDirectoryFilter = {
    freeText: string;
    type: CustomerType;
    active: boolean | null;
    transportProvider: string;
    country: string;
    materialPriceList: string;
    actionPriceList: string;
};

export type SupplierListFilter = {
    freeText: string;
    active: boolean | null;
};

export type ActionBasedActionListFilters = {
    freetext: string;
    mainCategory: string;
    productionTemplate?: string;
    active?: boolean;
};

export enum CustomerType {
    ALL = 'ALL',
    DENTHUB_CONNECTED = 'DENTHUB_CONNECTED',
    MY_CLIENT = 'MY_CLIENT',
    WHITE_LABEL = 'WHITE_LABEL',
    SUPPLIER = 'SUPPLIER',
}

export enum ClientStatus {
    SHOW_ACTIVE = 'SHOW_ACTIVE',
    SHOW_INACTIVE = 'SHOW_INACTIVE',
    SHOW_ALL = 'SHOW_ALL',
}

export type ClientContent = {
    name?: string;
    customerType?: CustomerType;
    active?: boolean;
    isMain?: boolean;
    contact?: OrganizationContact;
    isContactOverridden?: boolean;
    overriddenContact?: OrganizationContact;
};

export type ClSettings = {
    sendDeliveryNotesByEmail?: boolean;
    deliveryNotesEmailType?: 'dentist' | 'shared';
    sharedEmails?: string[];
};

export type LabClient = {
    _id: string;
    lab: Organization;
    labId: string;
    customerId: string;
    customer: Organization;
    isActive: boolean;
    name: string;
    ptjCustomerNumber?: string;
    invoiceEnabled?: boolean;
    internalDeliveryDaysAdvance?: number;
    externalDeliveryDaysAdvance?: number;
    materialListName?: string;
    actionListName?: string;
    actionBasedCurrency?: string;
    isWriteAllowed?: boolean;
    parentHasInvoiceConfig?: boolean;
    useOwnInvoiceSettings?: boolean;
    clientContent: ClientContent;
    invoiceSettings: InvoiceSettings;
    parentClinicId: string;
    transportProvider?: TransportProvider;
    deliveryInstructions?: string;
    clinicTransportPhone?: string;
    accountManager?: string;
    settings?: ClSettings;
    localeFormat?: LocaleFormat;
    // DD-3485: this lab's subcontract defaults for the supplier; resolves only for the
    // row's customer (the sending lab).
    subcontractDefaults?: SubcontractDefaults | null;
};

/** DD-3485 Marketplace: what the subcontract flow pre-selects for one partner. */
export type SubcontractDefaults = {
    includePatientReference: boolean;
    includePatientIdentityNumber: boolean;
    includeDentistName: boolean;
    includeAllRestorations: boolean;
    includeAttachments: boolean;
    includeScanCaseFiles: boolean;
    /** DD-3689: the parent's communication pre-selected for the message to this partner. */
    messageFields: SubcontractMessageField[];
    /** DD-3689: the language of the message to this partner; null = the sender's own. */
    messageLanguage: LocaleLanguage | null;
    /** DD-3689: the lab's users offered as responsible; one = pre-filled, none = every technician. */
    responsibleUserIds: string[];
};

/** DD-3689: the parts of the parent order's communication a message can be built from. */
export enum SubcontractMessageField {
    ORDER_COMMENTS = 'ORDER_COMMENTS',
    INTERNAL_NOTES = 'INTERNAL_NOTES',
    EXTERNAL_INSTRUCTIONS = 'EXTERNAL_INSTRUCTIONS',
    CHAT = 'CHAT',
    LAB_CHAT = 'LAB_CHAT',
}

export type DentistDirectoryFilter = {
    freeText: string;
    connectedType: DentistType;
    clinic: string;
};
